import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  clearStoredThreadMessages,
  createConversation,
  deleteConversation,
  fetchHistoryMessages,
  getStoredConversationId,
  listConversations,
  rememberThreadMessages,
  setStoredConversationId,
} from './PodAdapter.js';
import { PlusIcon } from './shared.jsx';

function conversationTitle(item) {
  const title = String(item?.title || item?.summary || '').replace(/\s+/g, ' ').trim();
  return title || 'New conversation';
}

function truncate(text, max) {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function mergeActiveConversation(conversations, activeId) {
  if (!activeId || conversations.some((item) => item.id === activeId)) return conversations;
  return [
    { id: activeId, title: 'Current conversation', updated_at: '', message_count: 0 },
    ...conversations,
  ];
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
         stroke="currentColor" strokeWidth="2.4"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
         stroke="currentColor" strokeWidth="2.4"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function ChatBubbleIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.14L4 20l1-4.2A8 8 0 1 1 21 12z" />
    </svg>
  );
}

function HistoryIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3.2-6.9" />
      <polyline points="3 4 3 9 8 9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SidebarPinIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="15" y1="4" x2="15" y2="20" />
    </svg>
  );
}

function SidebarFloatIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="6" width="11" height="12" rx="2" />
      <path d="M17 8h3v8h-3" />
    </svg>
  );
}

function TrashIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function ConversationSwitcher({ runtime, variant = 'page', mode = null, onToggleMode = null }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(() => getStoredConversationId() || '');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [popoverPos, setPopoverPos] = useState(null);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(
    () => mergeActiveConversation(conversations, activeId),
    [conversations, activeId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) => conversationTitle(item).toLowerCase().includes(q));
  }, [options, query]);

  const activeTitle = useMemo(() => {
    const found = options.find((item) => item.id === activeId);
    return truncate(conversationTitle(found || { title: 'New conversation' }), 48);
  }, [options, activeId]);

  const refresh = useCallback(async (signal) => {
    const list = await listConversations(signal);
    if (!signal?.aborted) setConversations(list);
  }, []);

  const updatePopoverPos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(Math.round(rect.width), 320);
    const left = Math.min(
      Math.max(8, Math.round(rect.left + rect.width / 2 - width / 2)),
      Math.max(8, window.innerWidth - width - 8),
    );
    setPopoverPos({ top: Math.round(rect.bottom + 6), left, width });
  }, []);

  const closePopover = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const openPopover = useCallback(() => {
    if (busy) return;
    updatePopoverPos();
    setQuery('');
    setHighlightIndex(0);
    setOpen(true);
  }, [busy, updatePopoverPos]);

  const togglePopover = useCallback(() => {
    if (open) closePopover();
    else openPopover();
  }, [open, openPopover, closePopover]);

  async function switchConversation(id) {
    const nextId = String(id || '').trim();
    if (!nextId || busy) return;
    if (nextId !== activeId) {
      setBusy(true);
      setActiveId(nextId);
      setStoredConversationId(nextId);
      try {
        const messages = await fetchHistoryMessages(nextId);
        rememberThreadMessages(messages);
        runtime?.thread?.reset(messages);
      } catch {
        clearStoredThreadMessages();
        try { runtime?.thread?.reset([]); } catch {}
      } finally {
        setBusy(false);
        refresh();
      }
    }
    closePopover();
  }

  async function handleDelete(itemId) {
    const id = String(itemId || '').trim();
    if (!id || busy) return;
    setBusy(true);
    try {
      const ok = await deleteConversation(id);
      if (!ok) return;
      let nextActiveId = activeId;
      const remaining = conversations.filter((item) => item.id !== id);
      setConversations(remaining);
      if (id === activeId) {
        const next = remaining[0]?.id || '';
        nextActiveId = next;
        setActiveId(next);
        setStoredConversationId(next);
        clearStoredThreadMessages();
        try { runtime?.thread?.reset([]); } catch {}
        if (next) {
          try {
            const messages = await fetchHistoryMessages(next);
            rememberThreadMessages(messages);
            runtime?.thread?.reset(messages);
          } catch {
            // already cleared above
          }
        }
      }
      // refresh in the background to pick up any server-side reordering
      refresh();
      // keep highlight in range
      setHighlightIndex((idx) => Math.min(idx, Math.max(remaining.length - 1, 0)));
      void nextActiveId;
    } finally {
      setBusy(false);
    }
  }

  async function startConversation() {
    if (busy) return;
    setBusy(true);
    try {
      const created = await createConversation();
      const nextId = created?.id;
      if (!nextId) return;
      setConversations((items) => [created, ...items.filter((item) => item.id !== nextId)]);
      setActiveId(nextId);
      setStoredConversationId(nextId);
      clearStoredThreadMessages();
      runtime?.thread?.reset([]);
    } finally {
      setBusy(false);
      closePopover();
    }
  }

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopoverPos();
    const onMove = () => updatePopoverPos();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePopoverPos]);

  // Header "+" button (or other Hub UI) can request a new conversation by
  // dispatching `hub:new-conversation`. Also auto-start when /dashboard?new=1
  // is loaded so navigation from non-chat pages still works.
  const startRef = useRef(null);
  startRef.current = startConversation;
  useEffect(() => {
    const onNew = () => { try { startRef.current?.(); } catch {} };
    window.addEventListener('hub:new-conversation', onNew);
    let urlNew = false;
    try {
      const params = new URLSearchParams(window.location.search);
      urlNew = params.get('new') === '1';
      if (urlNew) {
        params.delete('new');
        const qs = params.toString();
        const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        history.replaceState({}, '', url);
      }
    } catch {}
    if (urlNew) onNew();
    return () => window.removeEventListener('hub:new-conversation', onNew);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (event) => {
      const t = event.target;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      closePopover();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePopover();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closePopover]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    const onConversationChanged = (event) => {
      const id = event?.detail?.conversationId || getStoredConversationId() || '';
      setActiveId(id);
      refresh();
    };
    window.addEventListener('hub:assistant-conversation-changed', onConversationChanged);
    return () => window.removeEventListener('hub:assistant-conversation-changed', onConversationChanged);
  }, [refresh]);

  function onInputKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((idx) => Math.min(idx + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((idx) => Math.max(idx - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = filtered[highlightIndex];
      if (item) switchConversation(item.id);
    } else if (event.key === 'Tab') {
      closePopover();
    }
  }

  const className = variant === 'panel'
    ? 'hub-conversation-switcher hub-conversation-switcher-panel'
    : 'hub-conversation-switcher hub-conversation-switcher-page';

  const popover = open && popoverPos ? createPortal(
    <div
      className="hub-conversation-popover"
      role="dialog"
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: `${popoverPos.top}px`,
        left: `${popoverPos.left}px`,
        width: `${popoverPos.width}px`,
        zIndex: 10050,
      }}
    >
      <div className="hub-conversation-search">
        <span className="hub-conversation-search-icon" aria-hidden="true"><SearchIcon /></span>
        <input
          ref={inputRef}
          type="text"
          className="hub-conversation-search-input"
          placeholder="Search conversations…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onInputKeyDown}
          autoComplete="off"
          spellCheck="false"
          aria-label="Search conversations"
          aria-autocomplete="list"
          aria-controls="hub-conversation-listbox"
          aria-activedescendant={filtered[highlightIndex] ? `hub-conversation-option-${filtered[highlightIndex].id}` : undefined}
        />
      </div>
      <ul
        id="hub-conversation-listbox"
        className="hub-conversation-list"
        role="listbox"
      >
        {filtered.length === 0 && (
          <li className="hub-conversation-empty" role="presentation">
            No matching conversations
          </li>
        )}
        {filtered.map((item, index) => {
          const title = conversationTitle(item);
          const isActive = item.id === activeId;
          const isHighlighted = index === highlightIndex;
          const classes = ['hub-conversation-option'];
          if (isHighlighted) classes.push('is-highlighted');
          if (isActive) classes.push('is-active');
          return (
            <li
              key={item.id}
              id={`hub-conversation-option-${item.id}`}
              role="option"
              aria-selected={isActive}
              className={classes.join(' ')}
              onMouseEnter={() => setHighlightIndex(index)}
              onMouseDown={(event) => { event.preventDefault(); switchConversation(item.id); }}
            >
              <span className="hub-conversation-option-icon" aria-hidden="true">
                <ChatBubbleIcon />
              </span>
              <span className="hub-conversation-option-title">{truncate(title, 56)}</span>
              {isActive && (
                <span className="hub-conversation-option-check" aria-hidden="true">
                  <CheckIcon />
                </span>
              )}
              <button
                type="button"
                className="hub-conversation-option-delete"
                aria-label={`Delete conversation: ${title}`}
                title="Delete conversation"
                disabled={busy}
                onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDelete(item.id);
                }}
              >
                <TrashIcon />
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="hub-conversation-create"
        onMouseDown={(event) => { event.preventDefault(); startConversation(); }}
        disabled={busy}
      >
        <PlusIcon />
        <span>New conversation</span>
      </button>
    </div>,
    document.body,
  ) : null;

  return (
    <div className={className}>
      <button
        type="button"
        ref={triggerRef}
        className={`hub-conversation-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select conversation"
        onClick={togglePopover}
        disabled={busy}
      >
        <span className="hub-conversation-trigger-icon" aria-hidden="true">
          <HistoryIcon />
        </span>
        <span className="hub-conversation-trigger-label">{activeTitle}</span>
        <span className="hub-conversation-trigger-chevron" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </button>
      {onToggleMode && (
        <button
          type="button"
          className="hub-conversation-mode"
          onClick={onToggleMode}
          aria-label={mode === 'sidebar' ? 'Float as FAB' : 'Pin to sidebar'}
          title={mode === 'sidebar' ? 'Float as FAB' : 'Pin to sidebar'}
          aria-pressed={mode === 'sidebar'}
        >
          {mode === 'sidebar' ? <SidebarFloatIcon /> : <SidebarPinIcon />}
        </button>
      )}
      {popover}
    </div>
  );
}
